import React from "react";
import { Line } from "react-chartjs-2";
import "../tickers/table/ticker-modal/styles/LineChart.scss";
// import annotationPlugin from "chartjs-plugin-annotation";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
} from "chart.js";

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  // annotationPlugin,
);

Chart.defaults.set("plugins.datalabels", {
  display: false,
});

let width, height, gradient;

// function getGradient(ctx, chartArea, minVal, maxVal) {
function getGradient(ctx, chartArea) {
  const chartWidth = chartArea.right - chartArea.left;
  const chartHeight = chartArea.bottom - chartArea.top;
  if (gradient === null || width !== chartWidth || height !== chartHeight) {
    // Create the gradient because this is either the first render
    // or the size of the chart has changed.
    width = chartWidth;
    height = chartHeight;
    gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    gradient.addColorStop(0, "green");
    gradient.addColorStop(0.5, "yellow");
    gradient.addColorStop(1, "red");
  }

  return gradient;
}

export default function DoomsdayChart({
  title,
  info,
  values,
  indexes,
  labelsExist,
  labelsReverse,
  dates,
  yAxis,
  fullScreen,
  showGradient,
  signal,
}) {
  const [period, setPeriod] = React.useState("1Y");

  const findMinRange = (o, step) => {
    return Math.ceil((o + step / 100) / step) * step - step;
  };

  const findMaxRange = (o, step) => {
    return Math.floor((o - step / 100) / step) * step + step;
  };

  const stepDict = {
    "FEAR/GREED SIGNAL": 10,
    "MARKET MOMENTUM": 500,
    "MARKET STRENGTH": 10,
    "MARKET BREADTH": 1000,
    "EQUITY PUT/CALL RATIO": 0.2,
    VOLATILITY: 10,
    "JUNK BOND DEMAND": 1,
    "SAFE HAVEN DEMAND": 10,
    "VVIX/VIX": 1,
    "MONTHLY PMI": 0.5,
    "YIELD INVERSION": 0.2,
    "DOOMSDAY SIGNAL": 10,
  };

  const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const zeroDecimals = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const oneDecimal = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const twoDecimals = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const data = {
    labels:
      period === "1D"
        ? dates.slice(-5)
        : period === "1W"
        ? dates.slice(-10)
        : period === "1M"
        ? dates.slice(-21)
        : title === "EQUITY PUT/CALL RATIO"
        ? dates.slice(-126)
        : dates,
    datasets: [
      {
        datasetKeyProvider: title + "DS2",
        label:
          title === "MARKET MOMENTUM" || title === "YIELD INVERSION"
            ? period === "1D"
              ? indexes.slice(-5)
              : period === "1W"
              ? indexes.slice(-10)
              : period === "1M"
              ? indexes.slice(-21)
              : indexes
            : 0,
        data:
          title === "MARKET MOMENTUM" || title === "YIELD INVERSION"
            ? period === "1D"
              ? indexes.slice(-5)
              : period === "1W"
              ? indexes.slice(-10)
              : period === "1M"
              ? indexes.slice(-21)
              : indexes
            : 0,
        borderDash: title === "MARKET MOMENTUM" ? [10, 5] : [],
        borderColor:
          title === "MARKET MOMENTUM"
            ? "aqua"
            : title === "YIELD INVERSION"
            ? "rgba(255, 0, 0, 0.74)"
            : "rgba(0, 0, 0, 0)",
        backgroundColor: "rgba(255, 255, 255, 0)",
        pointRadius: 0,
      },
      {
        datasetKeyProvider: title + "DS1",
        label:
          period === "1D"
            ? values.slice(-5)
            : period === "1W"
            ? values.slice(-10)
            : period === "1M"
            ? values.slice(-21)
            : values,
        data:
          period === "1D"
            ? values.slice(-5)
            : period === "1W"
            ? values.slice(-10)
            : period === "1M"
            ? values.slice(-21)
            : title === "EQUITY PUT/CALL RATIO"
            ? values.slice(-126)
            : values,
        fill: title === "YIELD INVERSION" ? "start" : false,
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        pointRadius: 0,
        borderColor: showGradient
          ? function (context) {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                // This case happens on initial chart load
                return null;
              }
              return getGradient(ctx, chartArea);
            }
          : "#ffffff",
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    radius: 0,
    animation: {
      duration: 0,
    },
    backgroundColor: "white",
    maxBarThickness: 100,
    interaction: {
      mode: "index",
      intersect: false,
      axis: "x",
    },
    plugins: {
      annotation: {
        annotations: {
          top: {
            type: "label",
            xValue: (context) => {
              if (context?.chart?.scales?.x) {
                const shiftRight = labelsReverse ? 0.86 : 0.85;
                return (
                  (context.chart.scales.x.max - context.chart.scales.x.min) *
                    shiftRight +
                  context.chart.scales.x.min
                );
              }
              return 0;
            },
            yValue: (context) => {
              if (context?.chart?.scales?.y) {
                return (
                  (context.chart.scales.y.max - context.chart.scales.y.min) *
                    0.95 +
                  context.chart.scales.y.min
                );
              }
              return 0;
            },
            color: labelsReverse ? "#75CE41" : "#D33541",
            content: labelsExist
              ? (labelsReverse ? "Fear" : "Greed") + "▲"
              : "",
            font: {
              size: 18,
            },
          },
          bottom: {
            type: "label",
            color: labelsReverse ? "#D33541" : "#75CE41",
            content: labelsExist
              ? (labelsReverse ? "Greed" : "Fear") + "▼"
              : "",
            xValue: (context) => {
              if (context?.chart?.scales?.x) {
                const shiftRight = labelsReverse ? 0.85 : 0.86;
                return (
                  (context.chart.scales.x.max - context.chart.scales.x.min) *
                    shiftRight +
                  context.chart.scales.x.min
                );
              }
              return 0;
            },
            yValue: (context) => {
              if (context?.chart?.scales?.y) {
                return (
                  (context.chart.scales.y.max - context.chart.scales.y.min) *
                    0.05 +
                  context.chart.scales.y.min
                );
              }
              return 0;
            },
            font: {
              size: 18,
            },
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (
              context.dataset.datasetKeyProvider.startsWith("MARKET MOMENTUMDS")
            ) {
              const labelName =
                context.datasetIndex === 0 ? "125 DMA" : "SPX Index";
              return (
                context.parsed.y.toLocaleString("en-US") + ` (${labelName})`
              );
            }
            return context.parsed.y.toLocaleString("en-US");
          },
        },
      },
      legend: {
        display: true,
        labels: {
          color: "rgb(255, 99, 132)",
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "white",
          font: {
            family: "capsule",
          },
          minRotation: 0,
          maxRotation: 0,
          maxTicksLimit: 5,
          callback: function (value) {
            if (dates) {
              value = this.getLabelForValue(value);
              return new Date(value).toLocaleDateString();
            }
            return value;
          },
        },
      },
      y: {
        grid: {
          display: true,
          color: "#222222",
          drawBorder: true,
          // color: "#a9a9a9",
        },
        min:
          title === "FEAR/GREED SIGNAL" ||
          title === "MARKET STRENGTH" ||
          title === "OVERALL DOOMSDAY SIGNAL HISTORY"
            ? 0
            : Math.min(
                ...(values?.map((o) => findMinRange(o, stepDict[title])) ?? [
                  0,
                ]),
              ),
        // : findMinRange(Math.min(...values, stepDict[title])) ?? [0],
        max:
          title === "FEAR/GREED SIGNAL" ||
          title === "MARKET STRENGTH" ||
          title === "OVERALL DOOMSDAY SIGNAL HISTORY"
            ? 100
            : Math.max(
                ...(values?.map((o) => findMaxRange(o, stepDict[title])) ?? [
                  1,
                ]),
              ),
        // : findMaxRange(Math.max(...values, stepDict[title])) ?? [100],
        ticks: {
          color: "white",
          font: {
            family: "capsule",
          },
          maxTicksLimit: 5,
          callback: function (value) {
            if (title === "MARKET STRENGTH") return value + "%";
            else if (title === "MARKET BREADTH")
              return currency.format(value).replace("$", "");
            else if (
              title === "EQUITY PUT/CALL RATIO" ||
              title === "SAFE HAVEN DEMAND" ||
              title === "MONTHLY PMI"
            )
              return twoDecimals.format(value);
            else if (
              title === "JUNK BOND DEMAND" ||
              title === "VVIX/VIX" ||
              title === "YIELD INVERSION"
            )
              return oneDecimal.format(value);
            else if (title === "VOLATILITY") return zeroDecimals.format(value);
            else if (title === "MARKET MOMENTUM") return currency.format(value);
            else return value;
          },
        },
        title: {
          display: true,
          text: yAxis,
          // fontSize: "14",
          // fontColor: "white",
          font: {
            family: "capsule",
          },
        },
      },
    },
  };

  const constSignalToName = {
    "FEAR/GREED SIGNAL": "FEAR_GREED_NAME",
    VOLATILITY: "VOL_NAME",
    "VVIX/VIX": "VVIX_NAME",
    "MARKET MOMENTUM": "STK_PX_MOMO_NAME",
    "MARKET BREADTH": "STK_PX_BREADTH_NAME",
    "MARKET STRENGTH": "STK_PX_STRNGTH_NAME",
    "JUNK BOND DEMAND": "JUNK_NAME",
    "YIELD INVERSION": "INVERSION_NAME",
    "EQUITY PUT/CALL RATIO": "PUT_CALL_NAME",
    "SAFE HAVEN DEMAND": "SAFE_NAME",
    "GLOBAL PMI": "PMI_NAME",
    "OVERALL DOOMSDAY SIGNAL HISTORY": "DOOMSDAY_NAME",
  };

  const fearOrGreedName = (period) => {
    if (title && signal && signal[constSignalToName[title]]) {
      return signal[constSignalToName[title]][period];
    }
    return "Neutral";
  };

  const nameToColor = (name) => {
    switch (name) {
      case "Extreme Greed":
        return { color: "#ff2222" };
      case "Greed":
        return { color: "#ff6666" };
      case "Extreme Fear":
        return { color: "#75CE41" };
      case "Fear":
        return { color: "#4a8556" };
      case "Neutral":
      default:
        return { color: "#757575" };
    }
  };

  const fearOrGreedColor = (period) => {
    return nameToColor(fearOrGreedName(period));
  };

  const fearOrGreedSignal = (period) => {
    return signal ? fearOrGreedName(period) : "Neutral"; //"No Data"?
  };

  const fearOrGreedColor1 = fearOrGreedColor(1);
  const fearOrGreedColor5 = fearOrGreedColor(2);
  const fearOrGreedColor30 = fearOrGreedColor(3);
  const fearOrGreedColor365 = fearOrGreedColor(4);

  const fearOrGreedSignal1 = fearOrGreedSignal(1);
  const fearOrGreedSignal5 = fearOrGreedSignal(2);
  const fearOrGreedSignal30 = fearOrGreedSignal(3);
  const fearOrGreedSignal365 = fearOrGreedSignal(4);

  return (
    <div className="line-chart" style={{ color: "#ffffff" }}>
      <div className="top-section">
        <div
          className="long-bow-signal-title"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {title}
          <div
            data-tip={info}
            className="info-icon"
            style={{
              marginLeft: 10,
            }}
          >
            i
          </div>
        </div>
        {title === "MARKET BREADTH" && (
          <div style={{ float: "right", fontSize: "14px", fontColor: "white" }}>
            <span>Strong&nbsp;</span>
            <i className="fas fa-caret-up" />
            &nbsp;&nbsp;&nbsp;
            <span>Weak&nbsp;</span>
            <i className="fas fa-caret-down" />
          </div>
        )}
      </div>
      <div
        style={
          fullScreen & !window.matchMedia("(max-width: 480px)").matches
            ? { height: 445, width: "95vw", marginBottom: 50 }
            : !fullScreen & !window.matchMedia("(max-width: 480px)").matches
            ? { height: 245, width: "31vw", marginBottom: 60 }
            : { height: 210, width: "80vw", marginBottom: 60 }
        }
      >
        <Line data={data} options={options} label={title} />
        <div className={fullScreen ? "periods full" : "periods"}>
          <div
            className="period"
            style={({ cursor: "pointer" }, fearOrGreedColor1)}
            onClick={() => setPeriod("1D")}
          >
            1D:&nbsp;&nbsp;
            <span> {fearOrGreedSignal1} </span>
          </div>
          <div
            className="period"
            style={({ cursor: "pointer" }, fearOrGreedColor5)}
            onClick={() => setPeriod("1W")}
          >
            1W:&nbsp;&nbsp;
            <span> {fearOrGreedSignal5} </span>
          </div>
          <div
            className="period"
            style={({ cursor: "pointer" }, fearOrGreedColor30)}
            onClick={() => setPeriod("1M")}
          >
            1M:&nbsp;&nbsp;
            <span> {fearOrGreedSignal30} </span>
          </div>
          {title === "EQUITY PUT/CALL RATIO" && (
            <div
              className="period"
              style={({ cursor: "pointer" }, fearOrGreedColor365)}
              onClick={() => setPeriod("1Y")}
            >
              6M:&nbsp;&nbsp;
              <span> {fearOrGreedSignal365} </span>
            </div>
          )}
          {title !== "EQUITY PUT/CALL RATIO" && (
            <div
              className="period"
              style={({ cursor: "pointer" }, fearOrGreedColor365)}
              onClick={() => setPeriod("1Y")}
            >
              1Y:&nbsp;&nbsp;
              <span> {fearOrGreedSignal365} </span>
            </div>
          )}
        </div>
      </div>
      {/* <Tooltip /> */}
    </div>
  );
}
